/**
 * Conversation History Service
 * Integrates BigQuery conversation history with chatbot state management
 */

import { CookieHandler } from '@/Telemetry/cookie-init';
import {
  ConversationHistoryQuery,
  type ConversationHistoryVariables,
} from '@/graphql/queries/ConversationHistory.query';
import { type GCP_vertexGenAi_Query, VertexGenAiQuery } from '@/graphql/queries/GCP.query';
import type { ConversationHistory_Query } from '@/graphql/queries/__generated__/ConversationHistory_Query.graphql';
import { RelayEnvironment } from '@/graphql/relay-env';
import { VertexGenAiPromptType } from '@/graphql/types';
import { fetchQuery } from 'react-relay';
import { chatbotStateManager } from './chatbotStateManager';

interface ConversationContextParams {
  conversationHistory?: string;
  previousTopics?: string;
  lastInteraction?: string;
  sessionContext?: string;
  userType?: string;
}

class ConversationHistoryService {
  private static instance: ConversationHistoryService;
  private cachedHistory: {
    messages: Array<{
      id: string;
      message: string;
      source: string;
      timestamp: string;
      sessionId: string;
      intentName?: string;
      confidenceScore?: number;
    }>;
    totalMessages: number;
  } | null = null;
  private cacheTimestamp = 0;
  private readonly CACHE_DURATION = 30000; // 30 seconds

  private constructor() {}

  public static getInstance(): ConversationHistoryService {
    if (!ConversationHistoryService.instance) {
      ConversationHistoryService.instance = new ConversationHistoryService();
    }
    return ConversationHistoryService.instance;
  }

  /**
   * Load conversation history from BigQuery for context
   */
  public async loadConversationHistory(
    options: {
      userId?: string;
      contactId?: string;
      sessionId?: string;
      limit?: number;
      days?: number;
    } = {}
  ): Promise<{
    summary: string | null;
    messages: Array<{
      id: string;
      message: string;
      source: string;
      timestamp: string;
      sessionId: string;
      intentName?: string;
      confidenceScore?: number;
    }>;
    totalMessages: number;
  } | null> {
    try {
      const userIdentifier = chatbotStateManager.getUserIdentifier();
      const currentSessionId = chatbotStateManager.getSessionId();

      const queryVariables: ConversationHistoryVariables = {
        userId: options.userId || userIdentifier,
        sessionId: options.sessionId || currentSessionId || undefined,
        contactId: options.contactId,
        limit: options.limit || 20,
        days: options.days || 30,
      };

      const result = await fetchQuery<ConversationHistory_Query>(
        RelayEnvironment,
        ConversationHistoryQuery,
        queryVariables
      ).toPromise();

      if (result?.conversationHistory?.data) {
        const filteredMessages = (result.conversationHistory.data.messages || []).filter(
          (
            msg
          ): msg is {
            id: string;
            message: string;
            source: string;
            timestamp: string;
            sessionId: string;
          } => !!(msg?.id && msg.message && msg.source && msg.timestamp && msg.sessionId)
        );

        const finalResult = {
          summary: result.conversationHistory.data.summary || null,
          messages: filteredMessages.map((msg) => ({ ...msg })),
          totalMessages: result.conversationHistory.data.totalMessages || 0,
        };

        // Cache the result for hasConversationHistory calls
        this.cachedHistory = finalResult;
        this.cacheTimestamp = Date.now();

        return finalResult;
      }
      return null;
    } catch (error) {
      // Error loading conversation history
      return null;
    }
  }

  /**
   * Generate session parameters for Dialogflow with conversation context
   */
  public async generateSessionParameters(): Promise<ConversationContextParams> {
    try {
      const conversationHistory = await this.loadConversationHistory();
      const localContext = chatbotStateManager.getConversationContext();

      const params: ConversationContextParams = {};

      // Add BigQuery conversation history summary
      if (conversationHistory?.summary) {
        params.conversationHistory = conversationHistory.summary;
      }

      // Add recent topics from local context
      if (localContext?.topics && localContext.topics.length > 0) {
        params.previousTopics = localContext.topics.slice(0, 5).join(', ');
      }

      // Add last interaction info
      if (conversationHistory?.messages && conversationHistory.messages.length > 0) {
        const lastMessage = conversationHistory.messages[0]; // Messages are ordered DESC
        params.lastInteraction = `Last message: "${lastMessage.message.substring(0, 100)}" on ${new Date(
          lastMessage.timestamp
        ).toLocaleDateString()}`;
      }

      // Add session context
      if (localContext?.lastConversationSummary) {
        params.sessionContext = localContext.lastConversationSummary;
      }

      // Add user type
      params.userType = CookieHandler.getLoggedInUserId() ? 'registered' : 'anonymous';

      return params;
    } catch (error) {
      // Error generating session parameters
      return {};
    }
  }

  /**
   * Create enhanced contextual greeting with BigQuery history
   */
  public async getEnhancedContextualGreeting(): Promise<string | null> {
    try {
      // Use the same loadConversationHistory call as hasConversationHistory to ensure consistency
      const conversationHistory = await this.loadConversationHistory({ limit: 5 });

      // Only show "remember user" logic if we have actual BigQuery conversation history
      if (!conversationHistory || !conversationHistory.messages || conversationHistory.messages.length === 0) {
        return null; // No BigQuery history, don't show remembering logic
      }

      return this.generateEnhancedGreetingFromHistory(conversationHistory);
    } catch (error) {
      // Error getting enhanced greeting
      return null;
    }
  }

  /**
   * Create enhanced contextual greeting from pre-loaded conversation history
   */
  public async generateEnhancedGreetingFromHistory(conversationHistory: {
    messages: Array<{ message: string; source: string; timestamp: string }>;
    totalMessages: number;
  }): Promise<string | null> {
    try {
      // Only show "remember user" logic if we have actual BigQuery conversation history
      if (!conversationHistory || !conversationHistory.messages || conversationHistory.messages.length === 0) {
        return null;
      }

      // Use Vertex AI to create a meaningful summary from the conversation messages
      const meaningfulSummary = await this.generateConversationSummaryWithVertexAI(conversationHistory.messages);

      if (meaningfulSummary) {
        const daysAgo = this.getDaysAgoFromLastMessage(conversationHistory.messages);

        let greeting = 'Welcome back!';

        // Remove trailing punctuation from summary to avoid double periods
        const cleanSummary = meaningfulSummary.replace(/[.!?]+$/, '');

        if (daysAgo <= 1) {
          greeting += ` Earlier today we were discussing ${cleanSummary}.`;
        } else if (daysAgo <= 7) {
          greeting += ` Last week we discussed ${cleanSummary}.`;
        } else {
          greeting += ` In our previous conversations, we covered ${cleanSummary}.`;
        }

        greeting += ' How can I continue helping you?';
        return greeting;
      }

      // If we can't create a meaningful summary, don't show remembering logic
      return null;
    } catch (error) {
      // Error generating enhanced greeting from history
      return null;
    }
  }

  /**
   * Store conversation message to BigQuery (via backend API)
   */
  public async storeMessage(message: {
    message: string;
    source: 'user' | 'bot';
    sessionId: string;
    intentName?: string;
    confidenceScore?: number;
  }): Promise<boolean> {
    try {
      const userIdentifier = chatbotStateManager.getUserIdentifier();

      // Store locally first
      await chatbotStateManager.logMessage({
        text: message.message,
        sender: message.source,
        intentName: message.intentName,
        confidenceScore: message.confidenceScore,
      });

      return true;
    } catch (error) {
      // Error storing message
      return false;
    }
  }

  /**
   * Check if user has conversation history
   */
  public async hasConversationHistory(): Promise<boolean> {
    try {
      // Use cached result if available and recent
      if (this.cachedHistory && Date.now() - this.cacheTimestamp < this.CACHE_DURATION) {
        return this.cachedHistory.totalMessages > 0;
      }
      const history = await this.loadConversationHistory({ limit: 1 });
      return !!(history && history.totalMessages > 0);
    } catch (error) {
      // Error checking conversation history
      return false;
    }
  }

  /**
   * Get conversation context for session parameters as a formatted string
   */
  public async getConversationContextString(): Promise<string | null> {
    try {
      const params = await this.generateSessionParameters();

      const contextParts: string[] = [];

      if (params.conversationHistory) {
        contextParts.push(`Previous conversations: ${params.conversationHistory}`);
      }

      if (params.previousTopics) {
        contextParts.push(`Recent topics: ${params.previousTopics}`);
      }

      if (params.lastInteraction) {
        contextParts.push(params.lastInteraction);
      }

      return contextParts.length > 0 ? contextParts.join(' | ') : null;
    } catch (error) {
      // Error getting conversation context string
      return null;
    }
  }

  /**
   * Generate conversation summary using Vertex AI
   */
  public async generateConversationSummaryWithVertexAI(
    messages: Array<{ message: string; source: string }>
  ): Promise<string | null> {
    try {
      if (!messages || messages.length === 0) {
        return null;
      }

      // Format conversation text for analysis
      const conversationText = messages.map((msg) => `${msg.source}: ${msg.message}`).join('\n');

      if (!conversationText.trim()) {
        return null;
      }

      // Use Vertex AI to generate summary of conversation
      const result = await fetchQuery<GCP_vertexGenAi_Query>(RelayEnvironment, VertexGenAiQuery, {
        textContent: conversationText,
        promptType: VertexGenAiPromptType.Summarise,
        customPrompt: 'Focus on the main topics and themes discussed. Keep the summary concise and conversational.',
      }).toPromise();

      const summaryText = result?.vertexGenAi?.summary;

      if (summaryText && summaryText.trim().length > 0) {
        // Clean up the summary text to make it more conversational
        const cleanedSummary = summaryText
          .replace(/^(Summary:|The main topics|This conversation|Here is a summary).*?:/i, '')
          .replace(/\n+/g, ' ')
          .trim();
        return cleanedSummary;
      }

      // Fallback to simple keyword analysis if Vertex AI fails
      return this.generateSimpleTopicSummary(messages);
    } catch (error) {
      // Fallback to simple keyword analysis
      return this.generateSimpleTopicSummary(messages);
    }
  }

  /**
   * Fallback method for simple topic extraction with enhanced keyword matching
   */
  private generateSimpleTopicSummary(messages: Array<{ message: string; source: string }>): string {
    try {
      const userMessages = messages.filter((msg) => msg.source === 'user').map((msg) => msg.message.toLowerCase());

      if (!userMessages || userMessages.length === 0) {
        return 'various topics';
      }

      const topics = new Set<string>();
      const text = userMessages.join(' ');

      // Enhanced topic keywords with broader matching
      const topicMap = {
        podcasts: ['podcast', 'episode', 'audio', 'listen', 'interview', 'show', 'episode'],
        videos: ['video', 'watch', 'recording', 'film', 'movie', 'clip', 'tutorial'],
        'content search': ['search', 'find', 'looking for', 'searching', 'locate', 'discover'],
        'client work': ['client', 'project', 'case study', 'work', 'business', 'service'],
        'help and support': ['help', 'support', 'how to', 'question', 'assist', 'guidance', 'advice'],
        pricing: ['price', 'cost', 'pricing', 'fee', 'payment', 'subscription', 'plan'],
        features: ['feature', 'functionality', 'capability', 'function', 'tool', 'option'],
        learning: ['learn', 'education', 'course', 'training', 'skill', 'knowledge'],
        technology: ['tech', 'software', 'app', 'platform', 'system', 'digital'],
        content: ['content', 'article', 'post', 'material', 'resource', 'information'],
      };

      // Find matching topics
      Object.entries(topicMap).forEach(([topic, keywords]) => {
        if (keywords.some((keyword) => text.includes(keyword))) {
          topics.add(topic);
        }
      });

      if (topics.size > 0) {
        const topicsList = Array.from(topics);
        return topicsList.length === 1
          ? topicsList[0]
          : `${topicsList.slice(0, -1).join(', ')} and ${topicsList[topicsList.length - 1]}`;
      }

      // If no keywords match, try to extract meaningful words from the conversation
      const meaningfulWords = text
        .split(' ')
        .filter(
          (word) =>
            word.length > 3 &&
            ![
              'what',
              'how',
              'where',
              'when',
              'why',
              'can',
              'you',
              'the',
              'and',
              'for',
              'with',
              'about',
              'this',
              'that',
              'have',
              'will',
              'would',
              'could',
              'should',
            ].includes(word)
        )
        .slice(0, 3);

      if (meaningfulWords.length > 0) {
        return meaningfulWords.join(' and ');
      }

      return 'various topics';
    } catch (error) {
      // Error in simple topic summary
      return 'various topics';
    }
  }

  /**
   * Helper to calculate days ago from last message
   */
  private getDaysAgoFromLastMessage(messages: Array<{ timestamp: string }>): number {
    if (!messages || messages.length === 0) return Number.POSITIVE_INFINITY;

    const lastMessageTime = new Date(messages[0].timestamp).getTime();
    const now = Date.now();
    const diffInDays = Math.floor((now - lastMessageTime) / (1000 * 60 * 60 * 24));

    return diffInDays;
  }
}

export const conversationHistoryService = ConversationHistoryService.getInstance();
export default ConversationHistoryService;
